/**
 * Runs the game: which run is active, resuming and persisting it, the daily attempt rule, sealing
 * at the reset, results and personal bests. Rendering and input live elsewhere.
 */
import {
  type Action,
  type Coord,
  dailySeed,
  dayNumber,
  expeditionNumber,
  Game,
  type Locale,
  type Mode,
  type RunRecord,
  shareMap,
  shareText,
} from "@mistsweeper/core";
import {
  clearTogether,
  loadTogetherBest,
  saveTogether,
  saveTogetherBest,
  type TogetherRun,
} from "./party.ts";
import {
  clearRun,
  type DailyResult,
  type EndlessBest,
  isAttempt,
  loadDailyResult,
  loadEndlessBest,
  loadRun,
  recordEndlessRun,
  saveDailyResult,
  saveEndlessBest,
  saveRun,
} from "./runs.ts";

export interface DeathInfo {
  readonly metres: number;
  readonly tiles: number;
  /** For daily runs: whether this run was the attempt, and the attempt's distance. */
  readonly daily: { readonly attempt: boolean; readonly attemptMetres: number } | null;
  /** For endless runs: whether this is a new best, the best distance, and how many have ended. */
  readonly endless: {
    readonly newBest: boolean;
    readonly best: number;
    readonly runs: number;
  } | null;
  /** For a run played together: whether it is a new best together, and that best. */
  readonly together: { readonly newBest: boolean; readonly best: number } | null;
}

export type ControllerEvent =
  | { readonly type: "run" }
  | {
      readonly type: "changed";
      readonly revealed: readonly Coord[];
      /** The tile the action was taken on: revealed, chorded, or bannered. */
      readonly at: Coord;
    }
  | { readonly type: "died"; readonly info: DeathInfo }
  /** In a party (decision 0043): a member fell and the others go on; `you` when it was you. */
  | { readonly type: "fell"; readonly member: number; readonly at: Coord; readonly you: boolean }
  | {
      readonly type: "dayChanged";
      readonly sealed: DailyResult | null;
      readonly nextExpedition: number;
    };

function randomSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] as number;
}

export class GameController {
  mode: Mode = "daily";
  game: Game;
  /** True while a daily run is the day's attempt, the one that counts. */
  attempt = false;
  /** The day whose reset was last announced, so it is announced once. */
  private announced: number | null = null;
  /**
   * The party this run is played in (decision 0043), or null alone. Its own actions are applied
   * at once and sent; the room's order is the truth, so `confirmed` holds what the room has sent
   * back and `pending` what is still on its way, and a run that went otherwise is rebuilt.
   */
  party: { readonly code: string; token: string; you: number } | null = null;
  /** The party's run, which `game` is while Endless shows it; kept going under Today too. */
  private partyGame: Game | null = null;
  private confirmed: Action[] = [];
  private pending: Action[] = [];
  /** Sends one of this member's actions to the room; set by whoever holds the line. */
  sendAction: ((action: Action) => void) | null = null;
  private readonly listener: (event: ControllerEvent) => void;

  constructor(listener: (event: ControllerEvent) => void) {
    this.listener = listener;
    this.game = Game.create({ seed: 0, mode: "endless", startedAt: 0 });
  }

  /** Starts or resumes a run in the given mode. */
  start(mode: Mode, now: number): void {
    this.mode = mode;
    if (mode === "daily") this.startDaily(now);
    else this.startEndless(now);
    this.listener({ type: "run" });
  }

  private startDaily(now: number): void {
    const day = dayNumber(now);
    const saved = loadRun("daily");
    if (saved && saved.day === day) {
      this.game = Game.fromRecord(saved);
      this.attempt = isAttempt(saved);
      return;
    }
    if (saved && saved.day !== null && isAttempt(saved))
      this.sealSaved(saved.day, Game.fromRecord(saved));
    this.attempt = loadDailyResult(day) === null;
    this.game = Game.create({ seed: dailySeed(day), mode: "daily", day, startedAt: now });
    this.persist();
  }

  private startEndless(now: number): void {
    if (this.party && this.partyGame) {
      this.game = this.partyGame;
      this.attempt = false;
      return;
    }
    const saved = loadRun("endless");
    if (saved && saved.actions.length > 0) {
      this.game = Game.fromRecord(saved);
      this.attempt = false;
      if (this.game.status === "playing") return;
    }
    this.game = Game.create({ seed: randomSeed(), mode: "endless", startedAt: now });
    this.attempt = false;
    this.persist();
  }

  /** An endless run on a chosen seed, not resumed from storage; for tests and reference frames. */
  startEndlessWithSeed(seed: number, now: number): void {
    this.mode = "endless";
    this.attempt = false;
    this.game = Game.create({ seed, mode: "endless", startedAt: now });
    this.listener({ type: "run" });
  }

  /**
   * The run becomes a party's (decision 0043): the host's run under way, or the run a guest steps
   * into, from the room's record. Kept under `run.together`; the host's run leaves `run.endless`,
   * since it is the party's now, and a guest's own run waits there untouched.
   */
  joinParty(together: TogetherRun, hostsRun = false): void {
    const host = hostsRun && this.party === null && this.mode === "endless";
    this.party = { code: together.code, token: together.token, you: together.you };
    this.mode = "endless";
    this.attempt = false;
    if (host && this.game.mode === "endless" && this.game.party === 1) clearRun("endless");
    this.partyGame = Game.fromRecord(together.record);
    this.game = this.partyGame;
    this.confirmed = [...together.record.actions];
    this.pending = [];
    this.persist();
    this.listener({ type: "run" });
  }

  /**
   * The party's run kept on this device, as the game opens: it waits under Endless, and shows
   * at once when Endless is the place that shows.
   */
  resumeParty(together: TogetherRun): void {
    this.party = { code: together.code, token: together.token, you: together.you };
    this.partyGame = Game.fromRecord(together.record);
    this.confirmed = [...together.record.actions];
    this.pending = [];
    if (this.mode !== "endless") return;
    this.game = this.partyGame;
    this.attempt = false;
    this.listener({ type: "run" });
  }

  /** Leaves the party: its run stays the room's, and the player is alone again. */
  leaveParty(now: number): void {
    if (!this.party) return;
    const shown = this.together;
    const watched = (this.party?.you ?? 0) < 0;
    this.party = null;
    this.partyGame = null;
    this.confirmed = [];
    this.pending = [];
    if (!watched) clearTogether();
    if (shown) this.start("endless", now);
  }

  /**
   * The room's record after a welcome, the first or after a lost line: the truth, with what this
   * member did meanwhile tried again on it and sent once more.
   */
  partyWelcome(you: number, token: string, record: RunRecord): void {
    if (!this.party || !this.partyGame) return;
    this.party.you = you;
    this.party.token = token;
    const waiting = this.pending;
    this.confirmed = [...record.actions];
    this.pending = [];
    this.rebuild(record.party ?? 1, waiting, true);
  }

  /** A member came: the run makes room for as many as the party has. */
  partySize(size: number): void {
    const game = this.partyGame;
    if (!this.party || !game) return;
    while (game.party < size && game.join() !== null) {}
    this.persistParty();
  }

  /** An action the room recorded, in the order every member applies it. */
  partyAction(action: Action): void {
    const game = this.partyGame;
    if (!this.party || !game) return;
    this.confirmed.push(action);
    const head = this.pending[0];
    if (head && sameAction(head, action)) {
      this.pending.shift();
      this.persistParty();
      return;
    }
    const member = action[3] ?? 0;
    if (this.pending.length === 0) {
      // Nothing of ours on the way: the action goes onto the run as it stands.
      this.partySize(member + 1);
      const [kind, x, y] = action;
      const falls = game.falls.length;
      const result = game.apply(action);
      if (!this.together) this.persistParty();
      else if (kind !== "o") this.applied(result, { x, y }, falls, false);
      else {
        // A member taken out touches no tile, but can end the run (core spec 6.8).
        this.persist();
        if (result.died) this.listener({ type: "died", info: this.deathInfo() });
      }
      return;
    }
    this.rebuild(Math.max(game.party, member + 1), this.pending, false);
  }

  /**
   * Builds the run again from what the room confirmed, then tries this member's waiting actions
   * on it, sending them again when `resend`. The tiles that came are announced as one change.
   */
  private rebuild(party: number, waiting: readonly Action[], resend: boolean): void {
    const before = this.partyGame;
    if (!before) return;
    const shown = this.together;
    const falls = before.falls.length;
    const record: RunRecord = {
      ...before.record,
      ...(party > 1 ? { party } : {}),
      actions: this.confirmed,
    };
    const game = Game.fromRecord(record);
    while (game.party < party && game.join() !== null) {}
    const me = this.party?.you ?? 0;
    this.pending = [];
    for (const action of waiting) {
      const result = game.apply([action[0], action[1], action[2], me]);
      if (!result.changed) continue;
      const recorded = game.actions[game.actions.length - 1] as Action;
      this.pending.push(recorded);
      if (resend) this.sendAction?.(recorded);
    }
    this.partyGame = game;
    this.persistParty();
    if (!shown) return;
    this.game = game;
    const revealed = game.revealedTiles().filter(({ x, y }) => !before.isRevealed(x, y));
    this.listener({ type: "changed", revealed, at: revealed[0] ?? { x: 0, y: 0 } });
    this.announceFalls(falls);
    if (before.status === "playing" && game.status === "dead") {
      this.listener({ type: "died", info: this.deathInfo() });
    }
  }

  /** Play again: a practice run on today's board, or a fresh endless world. */
  restart(now: number): void {
    if (this.together) {
      if (!this.watching) clearTogether();
      this.party = null;
      this.partyGame = null;
      this.confirmed = [];
      this.pending = [];
    }
    if (this.mode === "daily") {
      const day = dayNumber(now);
      this.attempt = loadDailyResult(day) === null;
      this.game = Game.create({ seed: dailySeed(day), mode: "daily", day, startedAt: now });
    } else {
      this.game = Game.create({ seed: randomSeed(), mode: "endless", startedAt: now });
    }
    this.persist();
    this.listener({ type: "run" });
  }

  /** A tap: reveal a hidden tile, chord a revealed one. */
  tap(x: number, y: number): void {
    if (this.game.isRevealed(x, y)) this.chord(x, y);
    else this.reveal(x, y);
  }

  reveal(x: number, y: number): void {
    const falls = this.game.falls.length;
    this.applied(this.game.reveal(x, y, this.me), { x, y }, falls, true);
  }

  chord(x: number, y: number): void {
    const falls = this.game.falls.length;
    this.applied(this.game.chord(x, y, this.me), { x, y }, falls, true);
  }

  toggleBanner(x: number, y: number): void {
    const falls = this.game.falls.length;
    this.applied(this.game.toggleBanner(x, y, this.me), { x, y }, falls, true);
  }

  /** Whether the run shown is the party's (decision 0043). */
  get together(): boolean {
    return this.party !== null && this.partyGame !== null && this.game === this.partyGame;
  }

  /** The member this player acts as in the run shown: 0 alone. */
  private get me(): number {
    return this.together ? (this.party?.you ?? 0) : 0;
  }

  /** Whether this player may act: the run goes on, and in a party they have not fallen. */
  get canAct(): boolean {
    if (this.game.status !== "playing") return false;
    if (this.watching) return false;
    return !this.together || !this.game.hasFallen(this.me);
  }

  /** Daniel watches a party, live or played again, and is no member of it (decision 0043). */
  get watching(): boolean {
    return this.together && (this.party?.you ?? 0) < 0;
  }

  private applied(
    result: { changed: boolean; revealed: readonly Coord[]; died: boolean },
    at: Coord,
    falls: number,
    own: boolean,
  ): void {
    if (!result.changed) return;
    if (own && this.together) {
      const recorded = this.game.actions[this.game.actions.length - 1] as Action;
      this.pending.push(recorded);
      this.sendAction?.(recorded);
    }
    this.persist();
    this.listener({ type: "changed", revealed: result.revealed, at });
    if (this.together) this.announceFalls(falls);
    if (result.died) this.listener({ type: "died", info: this.deathInfo() });
  }

  /** Every fall after the first `from`, but the one that ended the run, which `died` carries. */
  private announceFalls(from: number): void {
    const falls = this.game.falls;
    const last = this.game.status === "dead" ? falls.length - 1 : falls.length;
    for (let i = from; i < last; i += 1) {
      const f = falls[i];
      if (!f) continue;
      const you = this.party !== null && f.member === this.party.you;
      this.listener({ type: "fell", member: f.member, at: { x: f.x, y: f.y }, you });
    }
  }

  private deathInfo(): DeathInfo {
    const game = this.game;
    let daily: DeathInfo["daily"] = null;
    let endless: DeathInfo["endless"] = null;
    if (this.together) {
      // A best of its own (decision 0043): never the Endless best, never the Endless count.
      const best = loadTogetherBest();
      const newBest = !this.watching && (!best || game.metres > best.metres);
      if (newBest) {
        saveTogetherBest({
          v: 1,
          metres: game.metres,
          tiles: game.tiles,
          seed: game.seed,
          at: Date.now(),
        });
      }
      const together = { newBest, best: newBest ? game.metres : (best?.metres ?? game.metres) };
      return { metres: game.metres, tiles: game.tiles, daily: null, endless: null, together };
    }
    if (game.mode === "daily" && game.day !== null) {
      const existing = loadDailyResult(game.day);
      if (this.attempt && !existing) {
        saveDailyResult(this.result(game.day, "dead"));
        daily = { attempt: true, attemptMetres: game.metres };
      } else {
        daily = { attempt: false, attemptMetres: existing?.metres ?? game.metres };
      }
    } else {
      const { runs } = recordEndlessRun(game.metres);
      const best = loadEndlessBest();
      if (!best || game.metres > best.metres) {
        saveEndlessBest({
          v: 1,
          metres: game.metres,
          tiles: game.tiles,
          seed: game.seed,
          at: Date.now(),
        });
        endless = { newBest: true, best: game.metres, runs };
      } else {
        endless = { newBest: false, best: best.metres, runs };
      }
    }
    return { metres: game.metres, tiles: game.tiles, daily, endless, together: null };
  }

  private result(day: number, outcome: "dead" | "sealed"): DailyResult {
    return {
      v: 1,
      day,
      expedition: expeditionNumber(day),
      outcome,
      metres: this.game.metres,
      tiles: this.game.tiles,
      shareMap: shareMap(this.game),
      record: this.game.record,
    };
  }

  private sealSaved(day: number, game: Game): void {
    if (loadDailyResult(day) || game.status !== "playing") return;
    saveDailyResult({
      v: 1,
      day,
      expedition: expeditionNumber(day),
      outcome: "sealed",
      metres: game.metres,
      tiles: game.tiles,
      shareMap: shareMap(game),
      record: game.record,
    });
  }

  /**
   * Call every second or so: seals the attempt when the reset passes mid-run, and says so once.
   * The run shown stays the last day's until the player sets out on today's, so without the
   * day remembered every tick after the reset would say it again, and the sealed dialog came
   * back every second.
   */
  tick(now: number): void {
    if (this.mode !== "daily" || this.game.day === null) return;
    const today = dayNumber(now);
    if (this.game.day === today || this.announced === today) return;
    this.announced = today;
    let sealed: DailyResult | null = null;
    if (this.attempt && this.game.status === "playing" && !loadDailyResult(this.game.day)) {
      sealed = this.result(this.game.day, "sealed");
      saveDailyResult(sealed);
    }
    this.attempt = false;
    this.persist();
    this.listener({ type: "dayChanged", sealed, nextExpedition: expeditionNumber(today) });
  }

  private persist(): void {
    if (this.together) this.persistParty();
    else saveRun(this.game.record);
  }

  /** The party's run, under `run.together`, whichever run shows. */
  private persistParty(): void {
    // What is watched is kept by the room, never here.
    if (!this.party || !this.partyGame || this.party.you < 0) return;
    saveTogether({
      v: 1,
      code: this.party.code,
      token: this.party.token,
      you: this.party.you,
      record: this.partyGame.record,
    });
  }

  /** Forgets the saved run of a mode; used when a world should not be resumed. */
  forget(mode: Mode): void {
    clearRun(mode);
  }

  expedition(): number | null {
    return this.game.day === null ? null : expeditionNumber(this.game.day);
  }

  /** The text to share: the day's attempt when there is one, otherwise the current run. */
  shareTextFor(locale: Locale): string {
    if (this.game.mode === "daily" && this.game.day !== null) {
      const result = loadDailyResult(this.game.day);
      if (result) {
        const game = Game.fromRecord(result.record);
        return shareText(game, { locale, expedition: result.expedition });
      }
    }
    return shareText(this.game, { locale, expedition: this.expedition() });
  }

  endlessBest(): EndlessBest | null {
    return loadEndlessBest();
  }

  dailyResult(now: number): DailyResult | null {
    return loadDailyResult(dayNumber(now));
  }
}

function sameAction(a: Action, b: Action): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && (a[3] ?? 0) === (b[3] ?? 0);
}
